import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../hooks/useNotification';
import { dataFeedsAPI, inputSourcesAPI } from '../services/api';
import {
  Rss,
  Plus,
  Edit2,
  Trash2,
  Copy,
  RefreshCw,
  Eye,
  EyeOff,
  X,
  Check,
  ExternalLink,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const DataFeeds = () => {
  const { showSuccess, showError } = useNotification();
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collections, setCollections] = useState([]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingFeed, setEditingFeed] = useState(null); // null = create mode
  const [form, setForm] = useState({
    name: '',
    description: '',
    source: 'hosts',
    mongo_filter: '{}',
    jq_filter: '',
    projection: '',
    limit: 1000,
    enabled: true,
  });

  // Preview state
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Consumer info (shown after create)
  const [consumerInfo, setConsumerInfo] = useState(null);

  const fetchFeeds = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dataFeedsAPI.list();
      setFeeds(response.data.feeds || []);
    } catch (err) {
      showError('Не удалось загрузить фиды');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCollections = useCallback(async () => {
    try {
      const response = await inputSourcesAPI.getMongoCollections();
      setCollections(response.data.collections || []);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchFeeds();
    fetchCollections();
  }, [fetchFeeds, fetchCollections]);

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      source: 'hosts',
      mongo_filter: '{}',
      jq_filter: '',
      projection: '',
      limit: 1000,
      enabled: true,
    });
    setPreviewData(null);
    setEditingFeed(null);
  };

  const openCreateModal = () => {
    resetForm();
    setConsumerInfo(null);
    setShowModal(true);
  };

  const openEditModal = async (feed) => {
    try {
      const response = await dataFeedsAPI.get(feed.feed_id);
      const f = response.data.feed;
      setEditingFeed(f);
      setForm({
        name: f.name || '',
        description: f.description || '',
        source: f.source || 'hosts',
        mongo_filter: f.mongo_filter ? JSON.stringify(f.mongo_filter, null, 2) : '{}',
        jq_filter: f.jq_filter || '',
        projection: f.projection ? JSON.stringify(f.projection, null, 2) : '',
        limit: f.limit || 1000,
        enabled: f.enabled !== false,
      });
      setConsumerInfo({
        feed_id: f.feed_id,
        api_key: f.api_key,
      });
      setPreviewData(null);
      setShowModal(true);
    } catch (err) {
      showError('Не удалось загрузить фид');
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showError('Укажите название фида');
      return;
    }

    let mongoFilter = {};
    try {
      mongoFilter = JSON.parse(form.mongo_filter || '{}');
    } catch {
      showError('Невалидный JSON в MongoDB Filter');
      return;
    }

    let projection = null;
    if (form.projection.trim()) {
      try {
        projection = JSON.parse(form.projection);
      } catch {
        showError('Невалидный JSON в Projection');
        return;
      }
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      source: form.source,
      mongo_filter: mongoFilter,
      jq_filter: form.jq_filter.trim(),
      projection,
      limit: parseInt(form.limit) || 1000,
      enabled: form.enabled,
    };

    try {
      if (editingFeed) {
        await dataFeedsAPI.update(editingFeed.feed_id, payload);
        showSuccess('Фид обновлён');
      } else {
        const response = await dataFeedsAPI.create(payload);
        const { feed_id, api_key } = response.data;
        setConsumerInfo({ feed_id, api_key });
        setEditingFeed({ feed_id }); // switch to edit mode
        showSuccess('Фид создан');
      }
      fetchFeeds();
    } catch (err) {
      showError(err.response?.data?.message || 'Ошибка сохранения');
    }
  };

  const handleDelete = async (feedId) => {
    if (!window.confirm('Удалить этот фид? Внешние потребители потеряют доступ.')) return;
    try {
      await dataFeedsAPI.delete(feedId);
      showSuccess('Фид удалён');
      fetchFeeds();
      if (editingFeed?.feed_id === feedId) {
        setShowModal(false);
        resetForm();
      }
    } catch (err) {
      showError('Не удалось удалить фид');
    }
  };

  const handleRegenerateKey = async (feedId) => {
    if (!window.confirm('Перегенерировать ключ? Старый ключ перестанет работать.')) return;
    try {
      const response = await dataFeedsAPI.regenerateKey(feedId);
      const newKey = response.data.api_key;
      setConsumerInfo((prev) => prev ? { ...prev, api_key: newKey } : null);
      showSuccess('Ключ перегенерирован');
      fetchFeeds();
    } catch (err) {
      showError('Не удалось перегенерировать ключ');
    }
  };

  const handlePreview = async () => {
    const feedId = editingFeed?.feed_id;
    if (!feedId) {
      showError('Сначала сохраните фид');
      return;
    }

    // Save current form first
    let mongoFilter = {};
    try {
      mongoFilter = JSON.parse(form.mongo_filter || '{}');
    } catch {
      showError('Невалидный JSON в MongoDB Filter');
      return;
    }

    let projection = null;
    if (form.projection.trim()) {
      try {
        projection = JSON.parse(form.projection);
      } catch {
        showError('Невалидный JSON в Projection');
        return;
      }
    }

    // Save before preview to ensure latest config is used
    try {
      await dataFeedsAPI.update(feedId, {
        name: form.name.trim(),
        description: form.description.trim(),
        source: form.source,
        mongo_filter: mongoFilter,
        jq_filter: form.jq_filter.trim(),
        projection,
        limit: parseInt(form.limit) || 1000,
        enabled: form.enabled,
      });
    } catch (err) {
      showError(err.response?.data?.message || 'Ошибка сохранения перед preview');
      return;
    }

    try {
      setPreviewLoading(true);
      const response = await dataFeedsAPI.preview(feedId);
      setPreviewData(response.data);
    } catch (err) {
      showError(err.response?.data?.message || 'Ошибка preview');
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      showSuccess(`${label} скопировано`);
    }).catch(() => {
      showError('Не удалось скопировать');
    });
  };

  const getFeedUrl = (feedId) => `${API_URL}/api/feeds/${feedId}`;

  const getCurlExample = (feedId, apiKey) =>
    `curl -H "X-Feed-Key: ${apiKey}" "${getFeedUrl(feedId)}"`;

  // Source options
  const sourceOptions = [
    { value: 'config', label: 'Config.json (все хосты)' },
    { value: 'hosts', label: 'Hosts' },
    ...collections
      .filter((c) => c.name !== 'hosts' && c.name !== 'config')
      .map((c) => ({ value: c.name, label: c.name })),
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-icon">
          <Rss size={24} />
        </div>
        <div className="page-header-text">
          <h1>Data Feeds</h1>
          <p>Read-only API эндпоинты для внешних потребителей</p>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button className="btn btn-primary" onClick={openCreateModal} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Plus size={16} /> Создать фид
        </button>
      </div>

      {/* Feed list */}
      {loading ? (
        <div className="card" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          Загрузка...
        </div>
      ) : feeds.length === 0 ? (
        <div className="card" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          Нет настроенных фидов. Создайте первый для экспорта данных проекта.
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--bg-tertiary)' }}>
                <th style={thStyle}>Название</th>
                <th style={thStyle}>Источник</th>
                <th style={thStyle}>jq</th>
                <th style={thStyle}>Статус</th>
                <th style={thStyle}>Обращений</th>
                <th style={thStyle}>Последний доступ</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {feeds.map((feed) => (
                <tr key={feed.feed_id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{feed.name}</span>
                    {feed.description && (
                      <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '2px' }}>{feed.description}</div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '12px',
                      backgroundColor: 'var(--bg-tertiary)',
                      color: 'var(--text-secondary)',
                    }}>
                      {feed.source}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {feed.jq_filter ? (
                      <code style={{ fontSize: '12px', color: 'var(--color-primary)' }}>{feed.jq_filter.length > 30 ? feed.jq_filter.slice(0, 30) + '...' : feed.jq_filter}</code>
                    ) : (
                      <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>-</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '12px',
                      backgroundColor: feed.enabled ? '#10b98120' : '#ef444420',
                      color: feed.enabled ? '#10b981' : '#ef4444',
                      fontWeight: '500',
                    }}>
                      {feed.enabled ? <Check size={12} /> : <X size={12} />}
                      {feed.enabled ? 'Активен' : 'Выключен'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{feed.access_count || 0}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
                      {feed.last_accessed_at ? new Date(feed.last_accessed_at).toLocaleString() : 'Нет'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => copyToClipboard(getFeedUrl(feed.feed_id), 'URL')}
                        style={iconBtnStyle}
                        title="Скопировать URL"
                      >
                        <ExternalLink size={14} />
                      </button>
                      <button onClick={() => openEditModal(feed)} style={iconBtnStyle} title="Редактировать">
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(feed.feed_id)}
                        style={{ ...iconBtnStyle, color: '#ef4444' }}
                        title="Удалить"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={overlayStyle} onClick={() => setShowModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            {/* Modal header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', color: 'var(--text-primary)' }}>
                {editingFeed ? 'Редактировать фид' : 'Создать фид'}
              </h2>
              <button onClick={() => setShowModal(false)} style={iconBtnStyle}><X size={18} /></button>
            </div>

            {/* Form */}
            <div style={{ display: 'flex', gap: '24px' }}>
              {/* Left column - form */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Название *</label>
                  <input
                    style={inputStyle}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Название фида"
                  />
                </div>

                <div>
                  <label style={labelStyle}>Описание</label>
                  <input
                    style={inputStyle}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Опционально"
                  />
                </div>

                <div>
                  <label style={labelStyle}>Источник данных</label>
                  <select
                    style={inputStyle}
                    value={form.source}
                    onChange={(e) => setForm({ ...form, source: e.target.value })}
                  >
                    {sourceOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {form.source !== 'config' && (
                  <>
                    <div>
                      <label style={labelStyle}>MongoDB Filter (JSON)</label>
                      <textarea
                        style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '12px', minHeight: '60px' }}
                        value={form.mongo_filter}
                        onChange={(e) => setForm({ ...form, mongo_filter: e.target.value })}
                        placeholder='{"status": "active"}'
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Projection (JSON, опционально)</label>
                      <textarea
                        style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '12px', minHeight: '40px' }}
                        value={form.projection}
                        onChange={(e) => setForm({ ...form, projection: e.target.value })}
                        placeholder='{"host": 1, "tech": 1, "_id": 0}'
                      />
                    </div>
                  </>
                )}

                <div>
                  <label style={labelStyle}>jq Filter (опционально)</label>
                  <input
                    style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '12px' }}
                    value={form.jq_filter}
                    onChange={(e) => setForm({ ...form, jq_filter: e.target.value })}
                    placeholder='[.[] | .host]'
                  />
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Лимит записей</label>
                    <input
                      style={inputStyle}
                      type="number"
                      min="1"
                      max="10000"
                      value={form.limit}
                      onChange={(e) => setForm({ ...form, limit: e.target.value })}
                    />
                  </div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-primary)' }}>
                      <input
                        type="checkbox"
                        checked={form.enabled}
                        onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                      />
                      Включён
                    </label>
                  </div>
                </div>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button className="btn btn-primary" onClick={handleSave} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Check size={16} /> Сохранить
                  </button>
                  {editingFeed && (
                    <button className="btn btn-secondary" onClick={handlePreview} disabled={previewLoading} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {previewLoading ? '...' : <><Eye size={16} /> Preview</>}
                    </button>
                  )}
                </div>
              </div>

              {/* Right column - consumer info + preview */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Consumer info */}
                {consumerInfo && (
                  <div style={{
                    padding: '16px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    backgroundColor: 'var(--bg-tertiary)',
                  }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: '14px', color: 'var(--text-primary)' }}>Доступ для потребителя</h3>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ ...labelStyle, fontSize: '11px' }}>URL</label>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <input style={{ ...inputStyle, flex: 1, fontSize: '12px', fontFamily: 'monospace' }} readOnly value={getFeedUrl(consumerInfo.feed_id)} />
                        <button style={iconBtnStyle} onClick={() => copyToClipboard(getFeedUrl(consumerInfo.feed_id), 'URL')}><Copy size={14} /></button>
                      </div>
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ ...labelStyle, fontSize: '11px' }}>API Key (X-Feed-Key)</label>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <input style={{ ...inputStyle, flex: 1, fontSize: '12px', fontFamily: 'monospace' }} readOnly value={consumerInfo.api_key} />
                        <button style={iconBtnStyle} onClick={() => copyToClipboard(consumerInfo.api_key, 'API Key')}><Copy size={14} /></button>
                        <button
                          style={{ ...iconBtnStyle, color: 'var(--color-warning)' }}
                          title="Перегенерировать ключ"
                          onClick={() => handleRegenerateKey(consumerInfo.feed_id)}
                        >
                          <RefreshCw size={14} />
                        </button>
                      </div>
                    </div>

                    <div>
                      <label style={{ ...labelStyle, fontSize: '11px' }}>cURL</label>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <input
                          style={{ ...inputStyle, flex: 1, fontSize: '11px', fontFamily: 'monospace' }}
                          readOnly
                          value={getCurlExample(consumerInfo.feed_id, consumerInfo.api_key)}
                        />
                        <button style={iconBtnStyle} onClick={() => copyToClipboard(getCurlExample(consumerInfo.feed_id, consumerInfo.api_key), 'cURL')}><Copy size={14} /></button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Preview */}
                {previewData !== null && (
                  <div style={{
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    backgroundColor: 'var(--bg-tertiary)',
                    flex: 1,
                    minHeight: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>
                        Preview ({previewData.count} записей, макс 50)
                      </label>
                      <button style={iconBtnStyle} onClick={() => setPreviewData(null)}><X size={14} /></button>
                    </div>
                    <pre style={{
                      flex: 1,
                      margin: 0,
                      padding: '10px',
                      backgroundColor: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontFamily: 'monospace',
                      color: 'var(--text-secondary)',
                      overflow: 'auto',
                      maxHeight: '400px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                    }}>
                      {JSON.stringify(previewData.data, null, 2)}
                    </pre>
                  </div>
                )}

                {!consumerInfo && !previewData && (
                  <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: 'var(--text-tertiary)',
                    fontSize: '13px',
                    border: '1px dashed var(--border-color)',
                    borderRadius: '8px',
                  }}>
                    Сохраните фид, чтобы получить URL и API ключ для потребителя
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const thStyle = {
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: '600',
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '10px 14px',
  fontSize: '13px',
};

const iconBtnStyle = {
  padding: '6px',
  border: '1px solid var(--border-color)',
  borderRadius: '4px',
  backgroundColor: 'transparent',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
};

const modalStyle = {
  backgroundColor: 'var(--bg-secondary)',
  borderRadius: '12px',
  padding: '24px',
  width: '900px',
  maxWidth: '95vw',
  maxHeight: '90vh',
  overflow: 'auto',
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '500',
  color: 'var(--text-secondary)',
  marginBottom: '4px',
};

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
  backgroundColor: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  fontSize: '13px',
  boxSizing: 'border-box',
};

export default DataFeeds;
